import type { ConversationImportPreview } from './conversationImport';
import { emptyScenarioFormValues, type ScenarioFormValues } from './scenario-form';

export const AGENT_CASE_STATUSES = ['pending', 'attended', 'rejected'] as const;
export type AgentCaseStatus = (typeof AGENT_CASE_STATUSES)[number];

export const CASE_STATUS_LABELS: Record<AgentCaseStatus, string> = {
  pending: 'Pendiente',
  attended: 'Atendido',
  rejected: 'Rechazado',
};

export interface AgentCaseClient {
  id: number;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
}

export interface AgentCaseUser {
  id: number;
  name: string | null;
  email: string;
}

export interface AgentCase {
  id: number;
  status: AgentCaseStatus;
  detail: string;
  messageId: number;
  clientId: number;
  projectId: number;
  subProjectId: number | null;
  resolutionNote: string | null;
  resolvedBy: number | null;
  resolvedAt: string | null;
  agentTestScenarioId: number | null;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  client?: AgentCaseClient | null;
  createdByUser?: AgentCaseUser | null;
  resolvedByUser?: AgentCaseUser | null;
  agentTestScenario?: { id: number; name: string } | null;
}

export interface CasesListParams {
  projectId: number;
  status?: AgentCaseStatus;
  page: number;
  limit: number;
}

export interface CasesListResponse {
  data: AgentCase[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UpdateCaseBody {
  status?: Exclude<AgentCaseStatus, 'pending'>;
  resolutionNote?: string;
  agentTestScenarioId?: number;
}

export const caseClientName = (client: AgentCaseClient | null | undefined): string => {
  if (!client) return 'Cliente desconocido';
  const name = [client.firstName, client.lastName].filter(Boolean).join(' ').trim();
  return name.length > 0 ? name : client.phone ?? `Cliente #${client.id}`;
};

export const caseReporterName = (user: AgentCaseUser | null | undefined): string => {
  if (!user) return '—';
  return user.name && user.name.trim().length > 0 ? user.name : user.email;
};

export const promoteScenarioValues = (
  agentCase: AgentCase,
  conversation?: ConversationImportPreview
): ScenarioFormValues => {
  const base: ScenarioFormValues = {
    ...emptyScenarioFormValues(),
    name: `Evaluación #${agentCase.id} — ${caseClientName(agentCase.client)}`,
  };

  if (!conversation || conversation.turns.length === 0) return base;

  const mapped = conversation.turns.map((turn) =>
    turn.role === 'tool'
      ? {
          role: turn.role,
          content: typeof turn.toolResult === 'string' ? turn.toolResult : JSON.stringify(turn.toolResult ?? {}),
          toolName: turn.toolName ?? '',
          toolCallId: turn.toolCallId ?? '',
        }
      : { role: turn.role, content: turn.content, toolName: '', toolCallId: '' }
  );

  const toolStubs: Record<string, unknown> = {};
  for (const turn of conversation.turns) {
    if (turn.role === 'tool' && turn.toolName && turn.toolResult !== undefined) {
      toolStubs[turn.toolName] = turn.toolResult;
    }
  }

  return {
    ...base,
    turns: mapped,
    toolStubsText: Object.keys(toolStubs).length > 0 ? JSON.stringify(toolStubs, null, 2) : '',
  };
};
